const DEMO_LATENCY_MS = 200

const nowIso = () => new Date().toISOString()
const clone = (value) => JSON.parse(JSON.stringify(value))
const sleep = (ms = DEMO_LATENCY_MS) => new Promise((resolve) => setTimeout(resolve, ms))

const samplePorts = (podId) => {
  const sanitized = podId.replace(/[^a-z0-9]/gi, '').toLowerCase()
  return [
    {
      ip: '203.0.113.10',
      isIpPublic: true,
      privatePort: 8188,
      publicPort: 8188,
      type: 'HTTP',
      proxyUrl: `https://${sanitized}-8188.proxy.runpod.net/`
    },
    {
      ip: '203.0.113.10',
      isIpPublic: true,
      privatePort: 22,
      publicPort: 30222,
      type: 'SSH',
      proxyUrl: null
    }
  ]
}

const seedPods = () => {
  const createdAt = new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString()
  return [
    {
      id: 'demo-pod-1',
      name: 'ComfyUI Demo Pod',
      desiredStatus: 'RUNNING',
      podType: 'COMMUNITY',
      cloudType: 'community',
      imageName: 'ghcr.io/demo/comfyui:latest',
      costPerHr: 0.48,
      gpuCount: 1,
      gpuTypeId: 'A10G',
      memoryInGb: 24,
      vcpuCount: 12,
      containerDiskInGb: 40,
      volumeInGb: 60,
      uptimeSeconds: 60 * 60 * 4,
      createdAt,
      runtime: {
        uptimeInSeconds: 60 * 60 * 4,
        ports: samplePorts('demo-pod-1'),
        gpus: [{ id: '0', gpuUtilPercent: 42, memoryUtilPercent: 57 }],
        container: { cpuPercent: 18, memoryPercent: 46 }
      },
      machine: { podHostId: 'demo-host-1' }
    },
    {
      id: 'demo-pod-2',
      name: 'Restore Sandbox',
      desiredStatus: 'STOPPED',
      podType: 'COMMUNITY',
      cloudType: 'community',
      imageName: 'ghcr.io/demo/comfyui:latest',
      costPerHr: 0.32,
      gpuCount: 1,
      gpuTypeId: 'T4',
      memoryInGb: 16,
      vcpuCount: 8,
      containerDiskInGb: 30,
      volumeInGb: 50,
      uptimeSeconds: 0,
      createdAt,
      runtime: {
        uptimeInSeconds: 0,
        ports: samplePorts('demo-pod-2'),
        gpus: [],
        container: { cpuPercent: 0, memoryPercent: 0 }
      },
      machine: { podHostId: 'demo-host-2' }
    }
  ]
}

const seedProfiles = () => ({
  defaults: { cadence_minutes: 60, categories: ['workflows'] },
  profiles: [
    {
      id: 'prof-default',
      name: 'Hourly workflows',
      cadence_minutes: 60,
      categories: ['workflows'],
      s3_prefix: 'demo/hourly',
      retention_days: 3,
      notes: 'Demo profile for workflows'
    },
    {
      id: 'prof-models',
      name: 'Models weekly',
      cadence_minutes: 7200,
      categories: ['models'],
      s3_prefix: 'demo/models',
      retention_days: 30,
      notes: 'Demo profile for model snapshots'
    }
  ],
  bindings: {
    'demo-pod-1': 'prof-default'
  }
})

const seedRecipes = () => [
  {
    id: 'recipe-baseline',
    name: 'Baseline ComfyUI stack',
    description: 'Seeds workflows, checkpoints, and IP-adapter.',
    spec: {},
    autosave_profile_id: 'prof-default',
    tags: ['demo', 'comfyui'],
    owner_id: 'demo-owner',
    created_at: nowIso(),
    updated_at: nowIso(),
    metadata: {},
    notes: 'Used for restoring into new pods quickly.'
  },
  {
    id: 'recipe-models',
    name: 'Model bundle',
    description: 'Includes SDXL base and popular LoRAs.',
    spec: {},
    autosave_profile_id: 'prof-models',
    tags: ['models'],
    owner_id: 'demo-owner',
    created_at: nowIso(),
    updated_at: nowIso(),
    metadata: {},
    notes: 'Attach to GPU pods before running heavy jobs.'
  }
]

const seedGpuTypes = () => [
  {
    id: 'A10G',
    display_name: 'A10G 24GB',
    memory_gb: 24,
    secure_price: 0.95,
    community_price: 0.48,
    secure_spot_price: 0.52,
    community_spot_price: 0.30,
    available_secure: true,
    available_community: true,
    stock_status: 'Available',
    available_gpu_counts: [1, 2, 4],
    max_unreserved_gpu_count: 4,
    minimum_bid_price: 0.25,
    uninterruptable_price: 0.60
  },
  {
    id: 'T4',
    display_name: 'T4 16GB',
    memory_gb: 16,
    secure_price: 0.45,
    community_price: 0.22,
    secure_spot_price: 0.28,
    community_spot_price: 0.12,
    available_secure: true,
    available_community: true,
    stock_status: 'Available',
    available_gpu_counts: [1],
    max_unreserved_gpu_count: 1,
    minimum_bid_price: 0.08,
    uninterruptable_price: 0.30
  }
]

export const createMockApiClient = () => {
  const state = {
    pods: seedPods(),
    events: [
      { id: 'evt-1', created_at: nowIso(), event: 'demo.start', details: { message: 'Demo started' }, level: 'INFO' }
    ],
    restoreJobs: [],
    ...seedProfiles(),
    recipes: seedRecipes(),
    gpuTypes: seedGpuTypes()
  }

  const pushEvent = (event, details = {}, level = 'INFO') => {
    const entry = { id: `evt-${Date.now()}`, created_at: nowIso(), event, details, level }
    state.events.unshift(entry)
    state.events = state.events.slice(0, 100)
  }

  const computeCosts = () => {
    let active = 0
    let perHour = 0
    state.pods.forEach((pod) => {
      if (pod.desiredStatus === 'RUNNING') {
        active += 1
        perHour += pod.costPerHr || 0
      }
    })
    return {
      current_cost_per_hour: perHour,
      estimated_daily_cost: perHour * 24,
      estimated_monthly_cost: perHour * 24 * 30,
      active_pods: active,
      total_pods: state.pods.length,
      last_updated: nowIso()
    }
  }

  const readinessPayload = () => {
    const pods = state.pods.map((pod) => {
      const port = (pod.runtime?.ports || [])[0] || {}
      const host = port.ip || '203.0.113.10'
      const sshPort = port.publicPort || port.privatePort || 22
      return {
        pod_id: pod.id,
        pod_name: pod.name,
        status: pod.desiredStatus,
        ready: pod.desiredStatus === 'RUNNING',
        endpoint: {
          host,
          port: sshPort,
          proxy_url: port.proxyUrl,
          private_port: port.privatePort || sshPort,
          is_ip_public: port.isIpPublic ?? true
        },
        ssh_command: `ssh -i ~/.ssh/demo_key root@${host} -p ${sshPort}`
      }
    })
    return {
      pods,
      ssh_key_configured: true,
      ssh_key_path: '~/.ssh/demo_key',
      ssh_user: 'root',
      ssh_remote_path: '/workspace',
      bootstrap_command: 'demo mode — no SSH bootstrap required'
    }
  }

  const listEvents = () => ({ events: clone(state.events) })
  const listPods = () => ({ myself: { pods: clone(state.pods) } })
  const listProfiles = () => ({
    profiles: clone(state.profiles),
    bindings: clone(state.bindings),
    defaults: clone(state.defaults)
  })
  const listRecipes = () => ({ recipes: clone(state.recipes) })
  const listRestoreJobs = () => ({ jobs: clone(state.restoreJobs) })
  const listGpuTypes = () => ({ gpu_types: clone(state.gpuTypes) })
  const health = () => ({
    runpod_api: 'demo',
    aws_s3: 'demo',
    runpod_s3: 'demo',
    timestamp: nowIso()
  })

  const createPod = (payload = {}) => {
    const id = `demo-pod-${Math.random().toString(16).slice(2, 8)}`
    const newPod = {
      id,
      name: payload.name || 'New Demo Pod',
      desiredStatus: 'RUNNING',
      podType: payload.pod_type || 'COMMUNITY',
      imageName: payload.image_name || 'ghcr.io/demo/comfyui:latest',
      costPerHr: 0.28,
      gpuCount: payload.gpu_count || 1,
      gpuTypeId: payload.gpu_type_id || 'A10G',
      memoryInGb: payload.memory_gb || 24,
      vcpuCount: payload.vcpu_count || 12,
      containerDiskInGb: payload.container_disk_gb || 40,
      volumeInGb: payload.volume_gb || 60,
      uptimeSeconds: 0,
      createdAt: nowIso(),
      runtime: {
        uptimeInSeconds: 0,
        ports: samplePorts(id),
        gpus: [{ id: '0', gpuUtilPercent: 0, memoryUtilPercent: 0 }],
        container: { cpuPercent: 0, memoryPercent: 0 }
      },
      machine: { podHostId: 'demo-host' }
    }
    state.pods.unshift(newPod)
    pushEvent('demo.pod.created', { pod_id: id, name: newPod.name })
    return { status: 'success', data: clone(newPod) }
  }

  const updatePodStatus = (podId, status) => {
    const pod = state.pods.find((p) => p.id === podId)
    if (!pod) throw new Error('Pod not found')
    pod.desiredStatus = status
    pushEvent('demo.pod.updated', { pod_id: podId, status })
    return { status: 'success', data: clone(pod) }
  }

  const addProfile = (payload) => {
    const profile = {
      id: `prof-${Math.random().toString(16).slice(2, 8)}`,
      name: payload.name || 'New Profile',
      cadence_minutes: Number(payload.cadence_minutes) || 60,
      categories: (payload.categories || 'workflows')
        .toString()
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      s3_prefix: payload.s3_prefix || '',
      retention_days: payload.retention_days ? Number(payload.retention_days) : undefined,
      notes: payload.notes || ''
    }
    state.profiles.push(profile)
    pushEvent('demo.profile.created', { profile_id: profile.id })
    return { profile }
  }

  const updateProfile = (profileId, payload) => {
    const profile = state.profiles.find((p) => p.id === profileId)
    if (!profile) throw new Error('Profile not found')
    if (payload.categories) {
      profile.categories = payload.categories
        .toString()
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    }
    if (payload.name) profile.name = payload.name
    if (payload.cadence_minutes) profile.cadence_minutes = Number(payload.cadence_minutes)
    if (payload.s3_prefix !== undefined) profile.s3_prefix = payload.s3_prefix
    if (payload.retention_days !== undefined) profile.retention_days = Number(payload.retention_days)
    if (payload.notes !== undefined) profile.notes = payload.notes
    pushEvent('demo.profile.updated', { profile_id: profileId })
    return { profile: clone(profile) }
  }

  const deleteProfile = (profileId) => {
    state.profiles = state.profiles.filter((p) => p.id !== profileId)
    Object.keys(state.bindings).forEach((podId) => {
      if (state.bindings[podId] === profileId) {
        delete state.bindings[podId]
      }
    })
    pushEvent('demo.profile.deleted', { profile_id: profileId })
    return { status: 'deleted' }
  }

  const bindProfile = (podId, profileId) => {
    state.bindings[podId] = profileId
    pushEvent('demo.profile.bound', { pod_id: podId, profile_id: profileId })
    return { status: 'success' }
  }

  const createBackup = (payload) => {
    const backupId = `demo-backup-${Date.now()}`
    pushEvent('demo.backup.created', { backup_id: backupId, pod_id: payload.pod_id })
    return {
      backup_id: backupId,
      manifest: {
        name: backupId,
        categories: payload.categories || ['workflows'],
        timestamp: nowIso(),
        notes: payload.notes || 'Demo backup'
      }
    }
  }

  const createRestoreJob = (recipeId, payload) => {
    const job = {
      id: `restore-${Math.random().toString(16).slice(2, 8)}`,
      recipe_id: recipeId,
      pod_id: payload.pod_id,
      pod_name: payload.pod_name,
      snapshot_prefix: payload.snapshot_prefix,
      categories: payload.categories || ['workflows'],
      target_path: payload.target_path || '/workspace',
      status: payload.dry_run ? 'staged' : 'completed',
      staged_path: '/tmp/demo/staged',
      result: { pod_path: payload.target_path || '/workspace', stage_path: '/tmp/demo/staged' },
      logs: ['Restore started', 'Sync completed', 'Restore finished'],
      created_at: nowIso(),
      updated_at: nowIso(),
      completed_at: nowIso(),
      notes: payload.notes || ''
    }
    state.restoreJobs.unshift(job)
    pushEvent('demo.restore.created', { job_id: job.id, pod_id: payload.pod_id })
    return { job }
  }

  return {
    async get(endpoint) {
      await sleep()
      if (endpoint.startsWith('/pods/ssh-readiness')) return readinessPayload()
      if (endpoint === '/pods') return listPods()
      if (endpoint.startsWith('/events')) return listEvents()
      if (endpoint === '/costs') return computeCosts()
      if (endpoint === '/costs/gpu-pricing') return listGpuTypes()
      if (endpoint === '/automation/profiles') return listProfiles()
      if (endpoint === '/recipes') return listRecipes()
      if (endpoint === '/recipes/restore-jobs') return listRestoreJobs()
      if (endpoint === '/health') return health()
      throw new Error(`Mock endpoint not implemented: ${endpoint}`)
    },
    async post(endpoint, data = {}) {
      await sleep()
      if (endpoint === '/pods/create') return createPod(data)
      const actionMatch = endpoint.match(/^\/pods\/([^/]+)\/(resume|stop|terminate|start)$/)
      if (actionMatch) {
        const [, podId, action] = actionMatch
        const statusMap = {
          resume: 'RUNNING',
          start: 'RUNNING',
          stop: 'STOPPED',
          terminate: 'TERMINATED'
        }
        return updatePodStatus(podId, statusMap[action] || 'RUNNING')
      }
      const profileMatch = endpoint.match(/^\/pods\/([^/]+)\/profile$/)
      if (profileMatch) {
        const [, podId] = profileMatch
        return bindProfile(podId, data.profile_id)
      }
      if (endpoint === '/automation/profiles') return addProfile(data)
      const restoreMatch = endpoint.match(/^\/recipes\/([^/]+)\/restore$/)
      if (restoreMatch) {
        const [, recipeId] = restoreMatch
        return createRestoreJob(recipeId, data)
      }
      if (endpoint === '/backups/create') return createBackup(data)
      throw new Error(`Mock endpoint not implemented: ${endpoint}`)
    },
    async put(endpoint, data = {}) {
      await sleep()
      const profileMatch = endpoint.match(/^\/automation\/profiles\/([^/]+)$/)
      if (profileMatch) {
        const [, profileId] = profileMatch
        return updateProfile(profileId, data)
      }
      throw new Error(`Mock endpoint not implemented: ${endpoint}`)
    },
    async delete(endpoint) {
      await sleep()
      const profileMatch = endpoint.match(/^\/automation\/profiles\/([^/]+)$/)
      if (profileMatch) {
        const [, profileId] = profileMatch
        return deleteProfile(profileId)
      }
      throw new Error(`Mock endpoint not implemented: ${endpoint}`)
    }
  }
}

export const DEMO_MODE = (import.meta.env?.VITE_DEMO_MODE ?? 'true') === 'true'
export const DEMO_API_LABEL = 'Demo (mock data)'
